import { Box, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import { formatPublishedDate, formatRelativeDate } from "@ggs/utils"
import type { LinkType } from "../../graphql/types"
import type { FC, ResponsiveImageType, TagProps } from ".."
import { Link, ResponsiveImage, SvgIcon, Tags, WishlistButton } from ".."

export type BlogArticleTeaserProps = {
  title: string
  tags: TagProps[]
  author?: LinkType
  date: string
  image: ResponsiveImageType
  uri: string
  featured?: boolean
  type?: "blog" | "press"
  layout?: "vertical" | "horizontal"
  noBorder?: boolean
  liked?: boolean
  contentType?: string
  nid?: number
  onLike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
  onUnlike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
  componentType?: string
}

const BlogArticleTeaser: FC<BlogArticleTeaserProps> = ({
  title,
  tags,
  author,
  image,
  date,
  uri,
  type = "blog",
  featured = false,
  layout = "vertical",
  noBorder = false,
  liked,
  contentType,
  nid,
  onLike,
  onUnlike,
  componentType,
}) => {
  return (
    <Box
      position="relative"
      display="grid"
      gridTemplateColumns={{
        xs: "1fr",
        lg: layout === "vertical" ? "auto" : "clamp(0px, 43.83%, 317px) auto",
      }}
      sx={(theme) => ({
        "borderRadius": 1.5,
        "isolation": "isolate",
        "transition": theme.transitions.create("box-shadow"),

        "&:hover, &:focus-within": {
          "boxShadow": `1px 1px 12px 0px ${alpha(theme.palette["neutral.900"].main, 0.15)}`,
          ".arrow-btn": { opacity: 1, bottom: 24 },
          "img": {
            transform: "scale(1.05)",
          },
        },
      })}
    >
      <Box
        overflow="hidden"
        position="relative"
        borderRadius={1.5}
        sx={{
          ...(layout === "vertical" && {
            mb: -1.5,
          }),
          ...(layout === "horizontal" && {
            mb: { xs: -1.5, lg: "unset" },
            mr: { xs: "unset", lg: -1.5 },
          }),
        }}
      >
        <ResponsiveImage
          image={image}
          pictureProps={{ position: "relative" }}
          sx={{
            display: "block",
            aspectRatio: featured ? "16/10" : "994/775",
            transition: (theme) =>
              theme.transitions.create("transform", {
                duration: theme.transitions.duration.standard * 1.5,
              }),
          }}
        />
        <Box
          position="absolute"
          top={0}
          left={0}
          bottom={0}
          right={0}
          bgcolor={(theme) => alpha(theme.palette["neutral.900"].main, 0.02)}
        />

        {type === "blog" && (
          <Box sx={{ position: "absolute", top: 24, right: 24, zIndex: 2 }}>
            <WishlistButton
              liked={liked}
              onLike={() => (onLike ? onLike({ contentType, nid }, componentType) : null)}
              onUnlike={() => (onUnlike ? onUnlike({ contentType, nid }, componentType) : null)}
            />
          </Box>
        )}
        <Link type="overlay" href={uri} aria-label={title} title={title} zIndex={1} />
      </Box>

      <Stack
        bgcolor={(theme) => theme.palette["neutral.100"].main}
        sx={(theme) => ({
          borderRadius: {
            xs: theme.spacing(0, 0, 1.5, 1.5),
            lg:
              layout === "vertical" ? theme.spacing(0, 0, 1.5, 1.5) : theme.spacing(0, 1.5, 1.5, 0),
          },
          ...(!noBorder && {
            border: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
          }),
          ...(layout === "vertical" && {
            pt: 1.5,
            borderTop: 0,
          }),
          ...(layout === "horizontal" && {
            pl: { xs: "unset", lg: 1.5 },
            pt: { xs: 1.5, lg: "unset" },
            borderLeft: { lg: 0 },
          }),
        })}
      >
        {tags && tags.length > 0 && (
          <Tags
            tags={tags.map((tag) => ({ ...tag, color: "floralWhite", hasBorder: true }))}
            pt={{ xs: 3, md: 7 }}
            px={{ xs: 3, md: 7 }}
            autoWidth
          />
        )}

        <Box px={{ xs: 4, md: 7 }} py={10}>
          <Link
            type="base"
            variant="heading.md"
            href={uri}
            sx={(theme) => ({
              "color": theme.palette["neutral.700"].main,
              "transition": theme.transitions.create(["opacity", "color"]),
              "&:hover, &:focus-within": {
                outline: 0,
                color: theme.palette["neutral.800"].main,
              },

              ...(featured && {
                [theme.breakpoints.up("md")]: {
                  fontSize: 28,
                },
              }),
            })}
          >
            {title}
          </Link>
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          columnGap={{ xs: 5, md: 7 }}
          mt="auto"
          px={{ xs: 5, md: 7 }}
          borderTop={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
        >
          <Typography
            variant="body.sm"
            py={5}
            sx={{
              opacity: 0.7,
            }}
          >
            {type === "blog"
              ? formatRelativeDate(date, "MMM DD, YYYY")
              : formatPublishedDate(date, "MMM DD, YYYY")}
          </Typography>
          {type === "blog" && author?.title && (
            <Typography
              variant="body.sm"
              py={5}
              pl={{ xs: 5, md: 7 }}
              sx={{
                borderLeft: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
              }}
            >
              <Box component="span" sx={{ opacity: 0.7 }}>
                By{" "}
              </Box>
              <Link
                type="base"
                variant="body.sm"
                href={author.uri}
                title={author.title}
                sx={{
                  "opacity": 0.7,
                  "transition": (theme) => theme.transitions.create(["opacity", "color"]),
                  "&:hover": {
                    opacity: 1,
                    color: (theme) => theme.palette["neutral.800"].main,
                  },
                }}
              >
                {author.title}
              </Link>
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}

export default BlogArticleTeaser
